import * as React from 'react';

import { useQuery } from '@apollo/client';
import AllWorkspacesQuery, {
  AllWorkspacesQueryResponse,
} from '@graphql/queries/AllWorkspacesQuery';
import { Autocomplete, Box, TextField } from '@mui/material';
import FormSection from 'components/ReportRequestV2/FormSection';
import useClientList from 'hooks/useClientList';

import { StepProps } from './types';
import { matchClientToWorkspace } from '../model/clientMatch';
import { PROJECT_NAME_MAX_LENGTH } from '../model/constants';
import { setField } from '../model/reducer';
import { WorkspaceOption } from '../model/types';
import { validateProjectName } from '../model/validation';

interface Props extends StepProps {
  /** The workspace the user is signed into; pre-selected with its client when they match. */
  currentWorkspaceId?: number | null;
}

const ProjectStep: React.FC<Props> = ({
  state,
  dispatch,
  errors,
  currentWorkspaceId,
}) => {
  const clients = useClientList();
  const { data, loading } =
    useQuery<AllWorkspacesQueryResponse>(AllWorkspacesQuery);
  const [touched, setTouched] = React.useState(false);

  const workspaces = React.useMemo<WorkspaceOption[]>(
    () =>
      [...(data?.allProducers.nodes ?? [])]
        .map(({ id, name }) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data],
  );

  const clientNames = React.useMemo(
    () => clients.map((c) => c.value),
    [clients],
  );

  // The client this form filled in itself; it follows the workspace until the
  // user picks a client of their own.
  const autoClient = React.useRef<string | null>(null);

  // A fresh form starts with the signed-in workspace and its client, but only when
  // the client can be matched to the workspace by name; otherwise both stay empty
  // rather than guessing half of the pair. A client picked first is kept, and the
  // workspace is only filled in when it matches that client.
  const prefilled = React.useRef(false);
  React.useEffect(() => {
    if (
      prefilled.current ||
      !currentWorkspaceId ||
      workspaces.length === 0 ||
      clientNames.length === 0
    )
      return;
    prefilled.current = true;
    if (state.workspace) return;
    const current = workspaces.find((w) => w.id === Number(currentWorkspaceId));
    const client = current && matchClientToWorkspace(current.name, clientNames);
    if (
      !current ||
      !client ||
      (state.clientName && state.clientName !== client)
    )
      return;
    dispatch(setField('workspace', current));
    if (!state.clientName) {
      dispatch(setField('clientName', client));
      autoClient.current = client;
    }
  }, [
    workspaces,
    clientNames,
    currentWorkspaceId,
    state.workspace,
    state.clientName,
    dispatch,
  ]);

  const chooseWorkspace = (workspace: WorkspaceOption | null) => {
    dispatch(setField('workspace', workspace));
    const ownClient =
      !!state.clientName && state.clientName !== autoClient.current;
    if (ownClient) return;
    const client = workspace
      ? matchClientToWorkspace(workspace.name, clientNames)
      : null;
    if (client !== (state.clientName || null))
      dispatch(setField('clientName', client ?? ''));
    autoClient.current = client;
  };

  // Validate the name live once the user has interacted with it.
  const nameError =
    errors.projectName ??
    (touched ? validateProjectName(state.projectName) : undefined);

  return (
    <FormSection
      title="Project details"
      description="These identify the report in the report list and in the delivered files."
    >
      <Box sx={{ display: 'grid', gap: 3, maxWidth: 560 }}>
        <TextField
          label="Project name"
          required
          value={state.projectName}
          onChange={(e) => dispatch(setField('projectName', e.target.value))}
          onBlur={() => setTouched(true)}
          error={!!nameError}
          helperText={
            nameError ??
            `Letters, numbers, spaces, hyphens and underscores. ${state.projectName.length}/${PROJECT_NAME_MAX_LENGTH}`
          }
          inputProps={{ maxLength: PROJECT_NAME_MAX_LENGTH }}
        />
        <Autocomplete
          options={clients.map((c) => c.value)}
          value={state.clientName || null}
          onChange={(_, value) => {
            autoClient.current = null;
            dispatch(setField('clientName', value ?? ''));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client"
              required
              error={!!errors.clientName}
              helperText={
                errors.clientName ??
                'The client the report is delivered to. Filled in from the workspace when the names match.'
              }
            />
          )}
        />
        <Autocomplete
          options={workspaces}
          loading={loading}
          value={state.workspace}
          getOptionLabel={(w) => w.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, value) => chooseWorkspace(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Workspace"
              required
              error={!!errors.workspace}
              helperText={
                errors.workspace ??
                'Owns the report. Its products are allowed into the competitive set by default.'
              }
            />
          )}
        />
      </Box>
    </FormSection>
  );
};

export default ProjectStep;
